import { Schema } from "effect";
import { AnnotationId, EditProposalId, SuggestionId } from "./ids.ts";
import { Locator } from "./locator.ts";

// One finding: a server-minted id, the single cited rule, and a one-sentence
// rationale. Highlights only — an annotation never proposes a fix.
export const Annotation = Schema.Struct({
  annotationId: AnnotationId,
  rule: Schema.String,
  rationale: Schema.String,
});
export type Annotation = typeof Annotation.Type;

// How broad a problem a manual finding is about — chosen by the Expert when they raise it, and the
// same choice that decides what the finding anchors to (the composer builds a span locator for
// `selection`, a whole-section `region` for the altitude scopes). `selection` is the exact
// highlighted span; `module`/`cycle`/`unit-plan` name the enclosing heading section at that
// altitude. `level` is the CEFR grouping of unit-plans (derived at export, not a heading in the
// document), so a `level`-scoped finding is a label only — it anchors to the enclosing unit-plan.
// Optional on the finding: judge findings and legacy manual findings carry none.
export const AnnotationScope = Schema.Literal("selection", "module", "cycle", "unit-plan", "level");
export type AnnotationScope = typeof AnnotationScope.Type;

// One CriticMarkup mutation within a Suggestion, over a located region. A move is
// modelled as a `delete` + an `insert` in the same suggestion — there is no native
// move variant. (Structural edits are not suggestion ops: the outline drag-reorder
// was retired in ADR-0078, and an agent now restructures via the block tools.)
//
// KEPT on the retired `Locator` union (block-locator refactor plan U2 — not migrated to
// `SpanLocator`): `EditOperation` is embedded in the `suggestion.created`/`suggestion.amended`/
// `edit.proposed` audit events `audit.ts` explicitly marks "COMPAT RULE: do NOT delete or rename —
// old JSONL/Postgres logs must still decode", so its `locator` field must keep decoding whatever
// shape those historical events actually persisted. Separately, `del`/`substitute` locators here
// address an arbitrary free-form diff hunk (`apps/web/src/source-edit.ts`) or a whole section
// (`outline.ts`'s `sectionLocator`, used by the outline's Remove affordance) — neither is "unique
// text within one block", so `SpanLocator` would not be a faithful reshape even ignoring the compat
// rule. (That client-side caller is itself already dead: `POST /api/propose-edit` was deleted
// server-side in the collab pivot, D9 — cleanup is out of U2's scope; see U9 / a follow-up.)
export const Insert = Schema.TaggedStruct("insert", {
  locator: Locator,
  text: Schema.String,
});
export const Delete = Schema.TaggedStruct("delete", { locator: Locator });
export const Substitute = Schema.TaggedStruct("substitute", {
  locator: Locator,
  replacement: Schema.String,
});
export const EditOperation = Schema.Union(Insert, Delete, Substitute);
export type EditOperation = typeof EditOperation.Type;

export const insert = (locator: Locator, text: string): EditOperation =>
  Insert.make({ locator, text });
export const del = (locator: Locator): EditOperation => Delete.make({ locator });
export const substitute = (locator: Locator, replacement: string): EditOperation =>
  Substitute.make({ locator, replacement });

// An atomic group of 1..N edit operations under one server-minted id, carrying the
// annotation ids it resolves and a reason; applied or stripped together. `resolves` is
// **0..N** (relaxed from NonEmptyArray, plan 2026-07-06 Unit 4): a span-targeted direction
// anchors a manual Annotation first, and the suggestion resolves it (1..N, as before); a
// span-less direction — a chat instruction with no target span — lands with an **empty**
// `resolves`, whose provenance lives in the conversation/session record rather than an
// annotation. The collateral-⇒-resolved fold at write time still unions any contained
// pending annotation ids into `resolves` regardless of whether the caller declared any.
export const Suggestion = Schema.Struct({
  suggestionId: SuggestionId,
  resolves: Schema.Array(AnnotationId),
  reason: Schema.String,
  operations: Schema.NonEmptyArray(EditOperation),
});
export type Suggestion = typeof Suggestion.Type;

// An Expert-authored edit proposal: an atomic group of 1..N edit operations the Expert proposes
// directly (propose → confirm), under one server-minted id. Unlike a `Suggestion` it originates from
// no annotation — `sweeps` is the (possibly empty) set of pending annotations a delete/substitute op
// happens to enclose, which the edit clears when confirmed. `author` is always `expert` (the agent
// has no edit-proposal surface), so it is implicit and not stored.
export const EditProposal = Schema.Struct({
  editProposalId: EditProposalId,
  reason: Schema.String,
  operations: Schema.NonEmptyArray(EditOperation),
  sweeps: Schema.Array(AnnotationId),
});
export type EditProposal = typeof EditProposal.Type;

// A triage outcome for one kept annotation.
export const Suggest = Schema.TaggedStruct("suggest", { annotationId: AnnotationId });
export const CommentOnly = Schema.TaggedStruct("comment-only", {
  annotationId: AnnotationId,
  reason: Schema.String,
});
export const Withdraw = Schema.TaggedStruct("withdraw", {
  annotationId: AnnotationId,
  reason: Schema.String,
});
export const Disposition = Schema.Union(Suggest, CommentOnly, Withdraw);
export type Disposition = typeof Disposition.Type;

export const suggest = (annotationId: AnnotationId): Disposition => Suggest.make({ annotationId });
export const commentOnly = (annotationId: AnnotationId, reason: string): Disposition =>
  CommentOnly.make({ annotationId, reason });
export const withdraw = (annotationId: AnnotationId, reason: string): Disposition =>
  Withdraw.make({ annotationId, reason });
